import { IconButton, Tooltip } from '@mui/material';
import { Contrast } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { petMipViewSettingsState } from '~/state/atoms/viewerAtoms';
import { TITLE_BAR_ICON_SX } from '~/viewportGrid';
import { normalizedAzimuth } from './PetMipPane';

/** [azimuth, view-name message id, button caption]. */
const SNAP_VIEWS = [
    [0, 'petMip.snap.anterior', 'A'],
    [90, 'petMip.snap.left', 'L'],
    [180, 'petMip.snap.posterior', 'P'],
    [270, 'petMip.snap.right', 'R'],
] as const;

const PRESSED_SX = {
    color: 'primary.main',
    '&:hover': { color: 'primary.main' },
};

/** The PET projection's title-bar controls: view snaps and polarity. */
export default function PetMipTitleActions() {
    const intl = useIntl();
    const [settings, setSettings] = useRecoilState(petMipViewSettingsState);
    const azimuth = normalizedAzimuth(settings.azimuthDeg);
    const invertLabel = intl.formatMessage({ id: 'petMip.invert' });
    return (
        <>
            {SNAP_VIEWS.map(([snapAzimuth, labelId, caption]) => {
                const label = intl.formatMessage({ id: labelId });
                const isPressed = azimuth === snapAzimuth;
                return (
                    <Tooltip key={caption} title={label} placement="bottom">
                        <IconButton
                            size="small"
                            aria-label={label}
                            aria-pressed={isPressed}
                            onClick={() =>
                                setSettings((current) => ({
                                    ...current,
                                    azimuthDeg: snapAzimuth,
                                }))
                            }
                            data-testid={`pet-mip-snap-${caption}`}
                            sx={[TITLE_BAR_ICON_SX, isPressed && PRESSED_SX]}
                        >
                            {caption}
                        </IconButton>
                    </Tooltip>
                );
            })}
            <Tooltip title={invertLabel} placement="bottom">
                <IconButton
                    size="small"
                    aria-label={invertLabel}
                    aria-pressed={settings.isInverted}
                    onClick={() =>
                        setSettings((current) => ({
                            ...current,
                            isInverted: !current.isInverted,
                        }))
                    }
                    data-testid="pet-mip-invert"
                    sx={[TITLE_BAR_ICON_SX, settings.isInverted && PRESSED_SX]}
                >
                    <Contrast size={13} />
                </IconButton>
            </Tooltip>
        </>
    );
}
